import React, { createContext, useCallback, useState } from "react";
import { CompanyResponseType } from "./companyContext";
import { UserResponseType } from "./userContext";
import { useNavigate } from "react-router-dom";
import { getAuthHeaders, PATH_INFO } from "../pages/util/helper";

export const defaultDepartment: DepartmentResponseType = {
  id: 0,
  name: '',
  brand_name: '',
  memo: '',
  created_at: '',
  updated_at: '',
  created_by: '',
  updated_by: '',
  users: [],
  company: {},
}

export type DepartmentRequestType = {
  id?: number | null,
  name: string | null,
  brand_name: string | null,
  memo?: string | null,
}

export type DepartmentResponseType = {
  id: number,
  name: string,
  brand_name: string,
  memo: string,
  created_at?: string,
  updated_at?: string,
  created_by?: string,
  updated_by?: string,
  users: Partial<UserResponseType>[]
  company: Partial<CompanyResponseType>
}

export const DepartmentContext = createContext<{
  departmentList: DepartmentResponseType[]
  departmentDetail: DepartmentResponseType
}>({
  departmentList: [],
  departmentDetail: defaultDepartment
})

export const UpdateDepartmentContext = createContext<{
  refreshDepartmentList: (id: string) => Promise<void>
  refreshDepartmentDetail: (id: string) => Promise<void>
  registerDepartment: (id: string, value: DepartmentRequestType) => Promise<void>
  deleteDepartment: (id: string) => Promise<void>
}>({
  refreshDepartmentList: async (id: string) => { },
  refreshDepartmentDetail: async (id: string) => { },
  registerDepartment: async (id: string, value: DepartmentRequestType) => { },
  deleteDepartment: async (id: string) => { }
})

export function useDepartmentContext() {
  const [departmentList, setDepartmentList] = useState<DepartmentResponseType[]>([])
  const [departmentDetail, setDepartmentDetail] = useState<DepartmentResponseType>(defaultDepartment)
  const navigate = useNavigate()

  // 企業IDから部署一覧を取得
  const refreshDepartmentList = useCallback(async (id: string) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/company/${id}`, {
        headers,
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await res.json();
      if (Array.isArray(data)) {
        setDepartmentList(data);
      }
    } catch (error) {
      console.error("Error fetching department.");
    }
  }, [navigate])

  // 部署IDから部署情報を取得
  const refreshDepartmentDetail = useCallback(async (id: string) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/${id}`, {
        headers,
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error("Network response was not ok")
      }
      const data = await res.json()
      setDepartmentDetail(data)
    } catch (error) {
      console.error("Error fetching department detail")
    }
  }, [navigate])

  // 部署登録
  const registerDepartment = useCallback(async (company_id: string, value: DepartmentRequestType) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ value, company_id })
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error('error res')
      }

      refreshDepartmentList(company_id)
    } catch (error) {
      console.error("Error fetching department.")
    }
  }, [refreshDepartmentList, navigate])

  // 部署IDで部署削除
  const deleteDepartment = useCallback(async (id: string) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/${id}`, {
        method: 'DELETE',
        headers
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }
    } catch (error) {
      console.error('Error fetching department.')
    }
  }, [navigate])

  return {
    departmentList,
    departmentDetail,
    refreshDepartmentList,
    refreshDepartmentDetail,
    registerDepartment,
    deleteDepartment
  }
}

export function DepartmentProvider({ children }: { children: React.ReactNode }) {
  const { departmentList, departmentDetail, refreshDepartmentList, refreshDepartmentDetail, registerDepartment, deleteDepartment } = useDepartmentContext()

  return (
    <UpdateDepartmentContext.Provider value={{ refreshDepartmentList, refreshDepartmentDetail, registerDepartment, deleteDepartment }}>
      <DepartmentContext.Provider value={{ departmentList, departmentDetail }}>
        {children}
      </DepartmentContext.Provider>
    </UpdateDepartmentContext.Provider>
  )
}